import * as React from 'react'
import Button from '@mui/lab/LoadingButton'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemAvatar,
  Avatar,
  Alert,
  colors,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import {
  CapabilitiesSearchAndSelect,
  CircularProgressWithLabel,
  OrganizationSearchAndSelect,
  ResourceSearchAndSelect,
} from '@assembly/components'
import { Capability } from '../Capabilities/types'
import {
  Allocation,
  CreateAllocationForm,
  Frequency,
  RateUnit,
  Status,
  Type,
} from '@assembly/pages/AssemblyTemplates/types'
import { capitalize } from 'lodash'
import {
  Resource,
} from '@assembly/pages/Resources/types'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import ReceiptIcon from '@mui/icons-material/Receipt'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getApiErrorMsg, getMediaUrl, stringAvatar } from '@assembly/utils'
import {
  AssemblyLine,
  AssemblyLineForm,
  AssemblyLineStatus,
  ProjectTypes,
  ProjectTypeDescriptions,
  SKU,
  getSkuDisplayLabel,
  isOptionalAllocationsSku,
  isOptionalMembersSku,
  isUsageCreditsSku,
  DashboardMode,
  DashboardSettings,
} from './types'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { defaultCurrency, defaultTimezone } from '@assembly/constants'
import { Organization } from '../OrganizationManager/types'
import moment from 'moment-timezone'
import {
  createAssemblyLine,
  estimateStartDate,
  updateAssemblyLine,
  searchSubscriptionConfigs,
  getSubscriptionConfig,
  type AssemblyLineSubscriptionConfig,
} from '@assembly/api/assemblyLine'
import OrgMembersSearchAndSelect from '@assembly/components/OrganizationMembersSearchAndSelect'
import { TeamMember } from '../Teams/types'
import { timezones } from '@assembly/shared/timezones'
import OwnerSearchAndSelect from '@assembly/components/OwnerSearchAndSelect'
import { RoutePaths } from '@assembly/constants'
import { interpolateRouteUtil } from '@assembly/utils'
import { useNavigate } from 'react-router-dom'
import { currencySymbols } from '@assembly/shared/types/countriesAndCurrencies'
import { styled } from '@mui/material/styles'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { Media } from '@assembly/shared/types/media'
import { uploadMedia } from '@assembly/api/resources'

const EstimatedStartDateButton = styled(LoadingButton)(({ theme }) => ({
  height: '57px',
  minWidth: '40%',
}))

const EstimateAlert = styled(Alert)(() => ({
  marginTop: '10px',
  marginBottom: '25px',
}))

const SetupFeildsBox = styled(Box)(() => ({
  border: '2px solid',
  padding: '15px',
  borderColor: colors.grey[400],
  marginTop: '10px',
  borderRadius: '5px',
}))

const MainSectionLabel = styled(Typography)(() => ({
  fontSize: '22px',
  fontWeight: 500,
  marginBottom: '20px',
}))

interface CreateAssemblyLineDialogTitleProps {
  children?: React.ReactNode
  onClose: () => void
}

function CreateAssemblyLineDialogTitle(
  props: CreateAssemblyLineDialogTitleProps
) {
  const { children, onClose, ...other } = props

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
}

interface SubscriptionConfigAutocompleteInlineProps {
  value: string | null
  onChange: (id: string | null) => void
  error?: boolean
  helperText?: string
  /** When set, only configs for this product line are selectable. */
  targetSkuFilter?: 'FOUNDRY_APP' | 'ASSEMBLY_AGENT_SUBSCRIPTION'
}

function subscriptionConfigMatchesTargetSku(
  config: AssemblyLineSubscriptionConfig,
  targetSkuFilter: 'FOUNDRY_APP' | 'ASSEMBLY_AGENT_SUBSCRIPTION'
): boolean {
  const effectiveTarget =
    config.targetSku === 'FOUNDRY_APP' ? 'FOUNDRY_APP' : 'ASSEMBLY_AGENT_SUBSCRIPTION'
  return effectiveTarget === targetSkuFilter
}

function SubscriptionConfigAutocompleteInline(
  props: SubscriptionConfigAutocompleteInlineProps
) {
  const { value, onChange, error, helperText, targetSkuFilter } = props
  const [configOptions, setConfigOptions] = React.useState<AssemblyLineSubscriptionConfig[]>([])
  const [configSearch, setConfigSearch] = React.useState('')
  const [configLoading, setConfigLoading] = React.useState(false)
  const [loadedConfig, setLoadedConfig] = React.useState<AssemblyLineSubscriptionConfig | null>(null)
  const { addAlert } = useSnackbars()
  const searchConfigs = React.useCallback(
    async (search: string) => {
      setConfigLoading(true)
      try {
        const res = await searchSubscriptionConfigs(search, 0, 20)
        setConfigOptions(res.configs || [])
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error searching configs'
        addAlert({ message: msg, type: SnackbarType.Error })
      } finally {
        setConfigLoading(false)
      }
    },
    [addAlert]
  )
  const searchConfigsRef = React.useRef(searchConfigs)
  searchConfigsRef.current = searchConfigs
  React.useEffect(() => {
    const t = setTimeout(() => searchConfigsRef.current(configSearch), 300)
    return () => clearTimeout(t)
  }, [configSearch])
  React.useEffect(() => {
    if (value?.trim() && !configOptions.some((c) => c.id === value)) {
      getSubscriptionConfig(value).then((config) => {
        if (config) setLoadedConfig(config)
      })
    } else {
      setLoadedConfig(null)
    }
  }, [value])
  const syntheticConfig = value && !configOptions.some((c) => c.id === value) && !(loadedConfig?.id === value)
    ? { id: value, name: configSearch || value } as AssemblyLineSubscriptionConfig
    : null
  const selectedConfig =
    configOptions.find((c) => c.id === value) ?? (value && loadedConfig?.id === value ? loadedConfig : null) ?? syntheticConfig
  const options = React.useMemo(() => {
    let merged = configOptions
    if (loadedConfig && !configOptions.some((c) => c.id === loadedConfig.id)) {
      merged = [loadedConfig, ...configOptions]
    } else if (syntheticConfig) {
      merged = [syntheticConfig, ...configOptions]
    }
    if (targetSkuFilter) {
      merged = merged.filter((config) => subscriptionConfigMatchesTargetSku(config, targetSkuFilter))
    }
    return merged
  }, [configOptions, loadedConfig, syntheticConfig, targetSkuFilter])
  return (
    <Autocomplete<AssemblyLineSubscriptionConfig>
      options={options}
      getOptionLabel={(opt) => (opt.name ? `${opt.name}${opt.id ? ` (${opt.id})` : ''}` : opt.id || '')}
      isOptionEqualToValue={(opt, val) => opt.id === val?.id}
      value={selectedConfig}
      onChange={(_, v) => onChange(v?.id ?? null)}
      onInputChange={(_, v, reason) => { if (reason === 'input') setConfigSearch(v) }}
      loading={configLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Subscription Config"
          placeholder="Search by name..."
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {configLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

interface CreateAssemblyLineDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (assemblyLine: AssemblyLine) => void
  assemblyLine?: AssemblyLine
}

export default function CreateAssemblyLineDialog(
  props: CreateAssemblyLineDialogProps
) {
  const { open, onClose, assemblyLine, onSuccess } = props
  const navigate = useNavigate()
  const formInitialValues: AssemblyLineForm = {
    name: '',
    description: '',
    capabilities: [],
    allocations: [],
    organization: null,
    owner: null,
    invalid_owner: false,
    monthlySubscriptionCost: null,
    startDate: null,
    endDate: null,
    members: [],
    invalid_members: false,
    timezone: defaultTimezone,
    approved: false,
    approvedBy: '',
    status: AssemblyLineStatus.Initialized,
    statusMessage: '',
    enableProjectManagement: false,
    projectManagementLink: '',
    submitTaskLink: '',
    kanbanLink: '',
    teamID: '',
    projectID: '',
    sku: SKU.ASSEMBLY_PARTNERS,
    restricted: false,
    dashboardSettings: {
      mode: DashboardMode.SelfServiceAgents,
    },
    funding: {
      billingAmount: null,
      currency: defaultCurrency,
      frequency: null,
      startDate: null,
      endDate: null,
      lastSuccessfulPayment: null,
      status: null,
      statusMsg: '',
      setupLink: '',
      paymentLink: '',
      apContacts: [],
      projectType: ProjectTypes.Recurring,
      createContractorInvoices: true,
      disableExternalInvoiceIntegration: false,
      tokenPricing: null,
      metaData: {},
      invalid_billingAmount: false,
      invalid_frequency: false,
      invalid_startDate: false,
      invalid_endDate: false,
      invalid_status: false,
      invalid_projectType: false,
    },
    requirements: {},
    invalid_startDate: false,
    invalid_endDate: false,
    invalid_name: false,
    invalid_capabilities: false,
    invalid_allocations: false,
    invalid_description: false,
    invalid_monthlySubscriptionCost: false,
    invalid_organization: false,
    invalid_status: false,
    invalid_sku: false,
    assemblyLineSubscriptionConfigId: null,
    invalid_assemblyLineSubscriptionConfigId: false,
  }
  const [formValues, setFormValues] = React.useState(formInitialValues)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [isAllocationFormVisible, setIsAllocationFormVisible] =
    React.useState<boolean>(false)
  const allocationFormInitialValues: CreateAllocationForm = {
    resourceID: '',
    amount: null,
    rate: 160,
    rateUnit: RateUnit.Hours,
    frequency: Frequency.Monthly,
    status: Status.Initialized,
    statusMessage: '',
    approved: false,
    approvedBy: '',
    active: false,
    startDate: '',
    startDateAccepted: false,
    type: Type.Contractor,
    invalid_resourceID: false,
    invalid_amount: false,
    invalid_rate: false,
    invalid_rateUnit: false,
    invalid_frequency: false,
    invalid_status: false,
    invalid_type: false,
  }
  const [allocationFormValues, setAllocationFormValues] =
    React.useState<CreateAllocationForm>(allocationFormInitialValues)
  const { addAlert } = useSnackbars()
  const [resource, setResource] = React.useState<Resource | null>(null)
  const [selectedAllocation, setSelectedAllocation] = React.useState<{
    allocation?: Allocation
    index: number
  }>({
    index: -1,
  })
  const [checkListItems, setCheckListItems] = React.useState({})
  const [estimatedDateTextVisible, setEstimatedDateTextVisible] =
    React.useState<boolean>(false)
  const [esitmatedMsg, setEstimatedMsg] = React.useState<string>('')
  const [estimatedStartDateLoading, setEstimatedStartDateLoading] =
    React.useState<boolean>(false)
  const [savedAssemblyLine, setSavedAssemblyLine] =
    React.useState<AssemblyLine | null>(assemblyLine as AssemblyLine)
  const [allocationsChanged, setAllocationsChanged] =
    React.useState<boolean>(false)
  const [isRequirementsUploading, setIsRequirementsUploading] =
    React.useState<boolean>(false)

  // Token Pricing UI state (for dollar amounts and dates)
  const [tokenPricingCreditsValueDollars, setTokenPricingCreditsValueDollars] = React.useState<string>('')
  const [tokenPricingCreditsStartTime, setTokenPricingCreditsStartTime] = React.useState<moment.Moment | null>(null)
  const [tokenPricingCreditsEndTime, setTokenPricingCreditsEndTime] = React.useState<moment.Moment | null>(null)

  const getCheckListItemLabel = (str: string) => {
    return str.split(' ').join('')
  }

  React.useEffect(() => {
    if (open) {
      if (assemblyLine) {
        setFormValues({
          ...formValues,
          ...assemblyLine,
          owner: assemblyLine.owner as TeamMember,
          startDate:
            assemblyLine.startDate && assemblyLine.startDate !== -1
              ? moment(assemblyLine.startDate)
              : null,
          endDate:
            assemblyLine.endDate && assemblyLine.startDate !== -1
              ? moment(assemblyLine.endDate)
              : null,
          organization: assemblyLine.org || null,
          enableProjectManagement:
            assemblyLine.projectManagement.enableProjectManagement ||
            !!assemblyLine.projectManagement.projectManagementLink ||
            !!assemblyLine.projectManagement.teamID ||
            !!assemblyLine.projectManagement.projectID ||
            false,
          projectManagementLink:
            assemblyLine.projectManagement.projectManagementLink,
          submitTaskLink: assemblyLine.projectManagement.submitTaskLink,
          kanbanLink: assemblyLine.projectManagement.kanbanLink,
          teamID: assemblyLine.projectManagement.teamID,
          projectID: assemblyLine.projectManagement.projectID,
          sku: assemblyLine.sku || SKU.ASSEMBLY_PARTNERS,
          restricted: assemblyLine.restricted || false,
          dashboardSettings: assemblyLine.dashboardSettings || {
            mode: DashboardMode.SelfServiceAgents,
          },
          assemblyLineSubscriptionConfigId: assemblyLine.assemblyLineSubscriptionConfigId ?? null,
          funding: {
            ...formValues.funding,
            ...assemblyLine.funding,
            projectType: assemblyLine.funding.projectType || ProjectTypes.Recurring,
            createContractorInvoices: assemblyLine.funding.createContractorInvoices !== undefined ? assemblyLine.funding.createContractorInvoices : true,
            disableExternalInvoiceIntegration: assemblyLine.funding.disableExternalInvoiceIntegration || false,
            tokenPricing: assemblyLine.funding.tokenPricing || null,
            metaData: assemblyLine.funding.metaData || {},
            startDate: assemblyLine.funding.startDate
              ? moment(Number(assemblyLine.funding.startDate))
              : null,
            endDate: assemblyLine.funding.endDate
              ? moment(Number(assemblyLine.funding.endDate))
              : null,
          },
          requirements: assemblyLine.requirements || {},
        })

        // Initialize UI state for token pricing
        if (assemblyLine.funding.tokenPricing) {
          setTokenPricingCreditsValueDollars(
            assemblyLine.funding.tokenPricing.totalCreditsMonetaryValue
              ? (assemblyLine.funding.tokenPricing.totalCreditsMonetaryValue / 100).toString()
              : ''
          )
          setTokenPricingCreditsStartTime(
            assemblyLine.funding.tokenPricing.creditsStartTime
              ? moment.unix(assemblyLine.funding.tokenPricing.creditsStartTime)
              : null
          )
          setTokenPricingCreditsEndTime(
            assemblyLine.funding.tokenPricing.creditsEndTime
              ? moment.unix(assemblyLine.funding.tokenPricing.creditsEndTime)
              : null
          )
        } else {
          setTokenPricingCreditsValueDollars('')
          setTokenPricingCreditsStartTime(null)
          setTokenPricingCreditsEndTime(null)
        }

        if (
          assemblyLine.status &&
          assemblyLine.checklists[assemblyLine.status]
        ) {
          const items = assemblyLine.checklists[assemblyLine.status]

          items.forEach((item: any) => {
            setCheckListItems((prev) => ({
              ...prev,
              [getCheckListItemLabel(item.title)]: {
                ...item,
                key: getCheckListItemLabel(item.title),
              },
            }))
          })
        }
      }
      setSavedAssemblyLine(assemblyLine as AssemblyLine)
    }
  }, [assemblyLine, open])

  const handleChangeFormField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = event.target
    const newValue = type === 'number' ? value || null : value

    // Check if this is a project management field that should auto-enable the toggle
    const isProjectManagementField = ['projectManagementLink', 'teamID', 'projectID'].includes(name)
    const shouldEnableProjectManagement = isProjectManagementField && value && value.trim() !== ''

    // Check if all project management fields are empty to auto-disable the toggle
    const updatedFormValues = {
      ...formValues,
      [name]: newValue,
      [`invalid_${name}`]: false,
    }

    const allProjectManagementFieldsEmpty =
      !updatedFormValues.projectManagementLink?.trim() &&
      !updatedFormValues.teamID?.trim() &&
      !updatedFormValues.projectID?.trim()

    setFormValues({
      ...updatedFormValues,
      // Auto-enable project management if any of the key fields have values
      ...(shouldEnableProjectManagement && { enableProjectManagement: true }),
      // Auto-disable project management if all fields are empty
      ...(isProjectManagementField && allProjectManagementFieldsEmpty && { enableProjectManagement: false }),
    })
  }

  const handleChangeFundingFormField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = event.target
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        [name]: type === 'number' ? value || null : value,
        [`invalid_${name}`]: false,
      },
    })
  }

  const handleChangeEnableProjectManagement = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormValues({
      ...formValues,
      enableProjectManagement: event.target.checked,
    })
  }

  const handleClickSave = async () => {
    const isFoundryApp = formValues.sku === SKU.FOUNDRY_APP
    const membersRequired = !isOptionalMembersSku(formValues.sku)
    const capabilitiesRequired = !isFoundryApp
    const monthlyCostRequired = !isFoundryApp

    if (
      formValues.organization === null ||
      formValues.name.length === 0 ||
      formValues.description.length === 0 ||
      (monthlyCostRequired && formValues.monthlySubscriptionCost === null) ||
      (capabilitiesRequired && formValues.capabilities.length === 0) ||
      formValues.funding.projectType === null ||
      formValues.funding.projectType.length === 0 ||
      (membersRequired && formValues.members.length === 0) ||
      formValues.owner === null ||
      !formValues.sku
    ) {
      setFormValues({
        ...formValues,
        invalid_organization: formValues.organization === null,
        invalid_name: formValues.name.length === 0,
        invalid_description: formValues.description.length === 0,
        invalid_monthlySubscriptionCost:
          monthlyCostRequired && formValues.monthlySubscriptionCost === null,
        invalid_capabilities: capabilitiesRequired && formValues.capabilities.length === 0,
        funding: {
          ...formValues.funding,
          invalid_projectType:
            formValues.funding.projectType === null ||
            formValues.funding.projectType.length === 0,
        },
        invalid_members: membersRequired && formValues.members.length === 0,
        invalid_owner: formValues.owner === null,
        invalid_sku: !formValues.sku,
      })
      addAlert({
        message: 'Please fill in all required fields',
        type: SnackbarType.Error,
      })
      return
    }
    setIsSaving(true)
    const payload = {
      orgID: formValues.organization?.id,
      name: formValues.name,
      description: formValues.description,
      monthlySubscriptionCost: Number(formValues.monthlySubscriptionCost),
      capabilities: formValues.capabilities.map((c) => c.id),
      allocations: formValues.allocations.map((allocation) => ({
        resourceID: allocation.resource?.id,
        amount: Number(allocation.amount),
        rate: Number(allocation.rate),
        rateUnit: allocation.rateUnit,
        frequency: allocation.frequency,
        status: allocation.status,
        statusMessage: allocation.statusMessage,
        approved: allocation.approved,
        approvedBy: allocation.approvedBy,
        active: allocation.active,
        type: allocation.type,
        startDate: allocation.startDate,
        startDateAccepted: allocation.startDateAccepted,
      })),
      startDate: moment(formValues.startDate).valueOf(),
      endDate: moment(formValues.endDate).valueOf(),
      timezone: formValues.timezone,
      funding: {
        billingAmount: formValues.funding.billingAmount,
        currency: formValues.funding.currency,
        frequency: formValues.funding.frequency,
        startDate: formValues.funding.startDate
          ? moment(formValues.funding.startDate).valueOf()
          : null,
        endDate: formValues.funding.endDate
          ? moment(formValues.funding.endDate).valueOf()
          : null,
        lastSuccessfulPayment: formValues.funding.lastSuccessfulPayment,
        status: formValues.funding.status,
        statusMsg: formValues.funding.statusMsg,
        setupLink: formValues.funding.setupLink,
        paymentLink: formValues.funding.paymentLink,
        apContacts: formValues.funding.apContacts,
        projectType: formValues.funding.projectType,
        createContractorInvoices: formValues.funding.createContractorInvoices,
        disableExternalInvoiceIntegration: formValues.funding.disableExternalInvoiceIntegration,
        tokenPricing: (() => {
          const tp = formValues.funding.tokenPricing
          // Build result object with all token pricing fields
          const result: any = {
            ...(tp || {}),
          }

          // Convert dollar amount to cents if provided
          if (tokenPricingCreditsValueDollars && tokenPricingCreditsValueDollars.trim() !== '') {
            const dollars = parseFloat(tokenPricingCreditsValueDollars)
            if (!isNaN(dollars)) {
              result.totalCreditsMonetaryValue = Math.round(dollars * 100)
            }
          }

          // Convert dates to Unix timestamps if provided
          if (tokenPricingCreditsStartTime) {
            result.creditsStartTime = tokenPricingCreditsStartTime.unix()
          }
          if (tokenPricingCreditsEndTime) {
            result.creditsEndTime = tokenPricingCreditsEndTime.unix()
          }

          // Check if tokenPricing has any non-null/non-undefined values
          const hasValues = Object.values(result).some(val => val !== null && val !== undefined)
          return hasValues ? result : null
        })(),
        metaData: Object.keys(formValues.funding.metaData || {}).reduce((acc: any, key) => {
          const value = formValues.funding.metaData?.[key as keyof typeof formValues.funding.metaData]
          if (value && value.trim() !== '') {
            acc[key] = value
          }
          return acc
        }, {}),
      },
      projectManagement: {
        enableProjectManagement: formValues.enableProjectManagement,
        projectManagementLink: formValues.projectManagementLink,
        submitTaskLink: formValues.submitTaskLink,
        kanbanLink: formValues.kanbanLink,
        teamID: formValues.teamID,
        projectID: formValues.projectID,
      },
      approved: formValues.approved,
      approvedBy: formValues.approvedBy,
      status: formValues.status,
      statusMessage: formValues.statusMessage,
      members: formValues.members.map((m) => m.id),
      owner: formValues.owner?.id,
      requirements: formValues.requirements,
      sku: formValues.sku,
      restricted: formValues.restricted,
      dashboardSettings: formValues.dashboardSettings,
      ...(formValues.assemblyLineSubscriptionConfigId?.trim() && {
        assemblyLineSubscriptionConfigId: formValues.assemblyLineSubscriptionConfigId.trim(),
      }),
    }

    if (savedAssemblyLine) {
      const checklists = {
        ...savedAssemblyLine.checklists,
      }

      if (savedAssemblyLine.status && savedAssemblyLine.status !== null) {
        checklists[savedAssemblyLine.status] = Object.values(
          checkListItems
        ).map((item: any) => ({
          title: item.title,
          description: item.description,
          done: item.done,
        }))
      }

      try {
        const { data } = await updateAssemblyLine({
          id: savedAssemblyLine.id,
          ...payload,
          checklists,
        })
        setIsSaving(false)
        onSuccess(data)
        addAlert({
          message: 'Assembly Line Updated!',
          type: SnackbarType.Success,
        })
        setSavedAssemblyLine(data)
        if (
          data.projectManagement &&
          Object.keys(data.projectManagement).length > 0
        ) {
          setFormValues({
            ...formValues,
            enableProjectManagement:
              data.projectManagement.enableProjectManagement ||
              !!data.projectManagement.projectManagementLink ||
              !!data.projectManagement.teamID ||
              !!data.projectManagement.projectID ||
              false,
            projectManagementLink:
              data.projectManagement.projectManagementLink || '',
            submitTaskLink: data.projectManagement.submitTaskLink || '',
            kanbanLink: data.projectManagement.kanbanLink || '',
            projectID: data.projectManagement.projectID || '',
            teamID: data.projectManagement.teamID || '',
            dashboardSettings: data.dashboardSettings || {
              mode: DashboardMode.SelfServiceAgents,
            },
          })
        } else {
          setFormValues({
            ...formValues,
            dashboardSettings: data.dashboardSettings || {
              mode: DashboardMode.SelfServiceAgents,
            },
          })
        }
        setAllocationsChanged(false)
      } catch (error: any) {
        setIsSaving(false)
        if (
          error.response &&
          error.response.data &&
          error.response.data.message &&
          typeof error.response.data.message === 'string'
        ) {
          addAlert({
            message: error.response.data.message,
            type: SnackbarType.Error,
          })
        }
      }
    } else {
      try {
        const { data } = await createAssemblyLine(payload)
        setIsSaving(false)
        onSuccess(data)
        addAlert({
          message: 'Assembly Line Created!',
          type: SnackbarType.Success,
        })
        setSavedAssemblyLine(data)
        // Update form values with the server response to ensure consistency
        setFormValues({
          ...formValues,
          ...data,
          owner: data.owner as TeamMember,
          startDate:
            data.startDate && data.startDate !== -1
              ? moment(data.startDate)
              : null,
          endDate:
            data.endDate && data.endDate !== -1
              ? moment(data.endDate)
              : null,
          organization: data.org || null,
          enableProjectManagement:
            data.projectManagement?.enableProjectManagement ||
            !!data.projectManagement?.projectManagementLink ||
            !!data.projectManagement?.teamID ||
            !!data.projectManagement?.projectID ||
            false,
          projectManagementLink:
            data.projectManagement?.projectManagementLink || '',
          submitTaskLink: data.projectManagement?.submitTaskLink || '',
          kanbanLink: data.projectManagement?.kanbanLink || '',
          projectID: data.projectManagement?.projectID || '',
          teamID: data.projectManagement?.teamID || '',
          projectType: data.projectType || ProjectTypes.Recurring,
          createContractorInvoices: data.createContractorInvoices !== undefined ? data.createContractorInvoices : true,
          sku: data.sku || SKU.ASSEMBLY_PARTNERS,
          dashboardSettings: data.dashboardSettings || {
            mode: DashboardMode.SelfServiceAgents,
          },
          funding: {
            ...formValues.funding,
            ...data.funding,
            startDate: data.funding?.startDate
              ? moment(Number(data.funding.startDate))
              : null,
            endDate: data.funding?.endDate
              ? moment(Number(data.funding.endDate))
              : null,
          },
          requirements: data.requirements || {},
        })
        setAllocationsChanged(false)
      } catch (error: any) {
        setIsSaving(false)
        if (
          error.response &&
          error.response.data &&
          error.response.data.message &&
          typeof error.response.data.message === 'string'
        ) {
          addAlert({
            message: error.response.data.message,
            type: SnackbarType.Error,
          })
        }
      }
    }
  }

  const handleChangeCapabilities = (capabilities: Capability[]) => {
    setFormValues({
      ...formValues,
      capabilities,
      invalid_capabilities: false,
    })
  }

  const handleChangeAllocationFormField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = event.target
    setAllocationFormValues({
      ...allocationFormValues,
      [name]: type === 'number' ? value || null : value,
      [`invalid_${name}`]: false,
    })
  }

  const handleChangeAllocationFormSelect = (event: SelectChangeEvent) => {
    const { name, value } = event.target
    setAllocationFormValues({
      ...allocationFormValues,
      [name]: value,
      [`invalid_${name}`]: false,
    })
  }

  const handleClose = () => {
    setFormValues(formInitialValues)
    setAllocationFormValues(allocationFormInitialValues)
    setIsAllocationFormVisible(false)
    setResource(null)
    setIsSaving(false)
    setCheckListItems({})
    setEstimatedDateTextVisible(false)
    setSavedAssemblyLine(null)
    onClose()
  }

  const handleSaveAllocation = () => {
    if (
      !resource ||
      allocationFormValues.amount === null ||
      allocationFormValues.rate === null ||
      allocationFormValues.frequency.length === 0 ||
      allocationFormValues.status.length === 0 ||
      allocationFormValues.type.length === 0 ||
      allocationFormValues.rateUnit.length === 0
    ) {
      setAllocationFormValues({
        ...allocationFormValues,
        invalid_resourceID: !resource,
        invalid_amount: allocationFormValues.amount === null,
        invalid_rate: allocationFormValues.rate === null,
        invalid_frequency: allocationFormValues.frequency.length === 0,
        invalid_status: allocationFormValues.status.length === 0,
        invalid_type: allocationFormValues.type.length === 0,
        invalid_rateUnit: allocationFormValues.rateUnit.length === 0,
      })
      return
    }

    const allocation = {
      resourceID: resource.id,
      resource: resource,
      amount: allocationFormValues.amount,
      rate: allocationFormValues.rate,
      rateUnit: allocationFormValues.rateUnit as RateUnit,
      frequency: allocationFormValues.frequency as Frequency,
      status: allocationFormValues.status as Status,
      statusMessage: allocationFormValues.statusMessage,
      type: allocationFormValues.type as Type,
      active: allocationFormValues.active,
      approved: allocationFormValues.approved,
      approvedBy: allocationFormValues.approvedBy,
      startDate: allocationFormValues.startDate,
      startDateAccepted: allocationFormValues.startDateAccepted,
    }

    if (selectedAllocation.allocation && selectedAllocation.index !== -1) {
      formValues.allocations[selectedAllocation.index] = allocation
    } else {
      formValues.allocations.push(allocation)
    }

    setFormValues({
      ...formValues,
      allocations: [...formValues.allocations],
    })
    setAllocationFormValues(allocationFormInitialValues)
    setResource(null)
    setIsAllocationFormVisible(false)
    setAllocationsChanged(true)
  }

  const handleChangeResource = (resource: Resource) => {
    setResource(resource)
    setAllocationFormValues({
      ...allocationFormValues,
      invalid_resourceID: false,
    })
  }

  const handleClickEditAllocation = (allocation: Allocation, index: number) => {
    setAllocationFormValues({
      ...allocationFormValues,
      ...allocation,
      status: allocation.status || Status.Initialized,
    })
    setSelectedAllocation({
      allocation,
      index,
    })
    setResource(allocation.resource || null)

    setIsAllocationFormVisible(true)
  }

  const handleClickDeleteAllocation = (index: number) => {
    formValues.allocations.splice(index, 1)
    setFormValues({
      ...formValues,
      allocations: [...formValues.allocations],
    })
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const { name, value } = event.target
    const nextValues: AssemblyLineForm = {
      ...formValues,
      [name]: value,
      [`invalid_${name}`]: false,
    } as AssemblyLineForm
    if (name === 'sku') {
      nextValues.assemblyLineSubscriptionConfigId = null
      nextValues.invalid_assemblyLineSubscriptionConfigId = false
    }
    setFormValues(nextValues)
  }

  const handleChangeOrganization = (organization: Organization) => {
    setFormValues({
      ...formValues,
      organization,
      members: [],
      owner: null,
      invalid_organization: false,
    })
  }

  const handleChangeStartDate = (date: any) => {
    let endDateInvalid = false

    if (formValues.endDate) {
      endDateInvalid = moment(formValues.endDate).isBefore(date)
    }

    setFormValues({
      ...formValues,
      startDate: date,
      invalid_startDate: false,
      invalid_endDate: endDateInvalid,
    })
  }

  const handleChangeEndDate = (date: any) => {
    setFormValues({
      ...formValues,
      endDate: date,
      invalid_endDate: false,
    })
  }


  const handleChangeFundingProjectType = (event: SelectChangeEvent) => {
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        projectType: event.target.value,
        invalid_projectType: false,
      },
    })
  }


  const handleChangeFundingCreateContractorInvoices = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        createContractorInvoices: event.target.checked,
      },
    })
  }

  const handleChangeFundingDisableExternalInvoiceIntegration = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        disableExternalInvoiceIntegration: event.target.checked,
      },
    })
  }

  const handleChangeTokenPricingField = (field: string, value: number | null) => {
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        tokenPricing: {
          ...(formValues.funding.tokenPricing || {}),
          [field]: value,
        } as any,
      },
    })
  }

  const handleChangeMetaDataField = (field: string, value: string) => {
    setFormValues({
      ...formValues,
      funding: {
        ...formValues.funding,
        metaData: {
          ...formValues.funding.metaData,
          [field]: value || undefined,
        },
      },
    })
  }

  const handleChangeMembers = (teamMembers: TeamMember[]) => {
    setFormValues({
      ...formValues,
      members: teamMembers,
      invalid_members: false,
    })
  }

  const handleChangeOwner = (teamMember: TeamMember) => {
    setFormValues({
      ...formValues,
      owner: teamMember,
      invalid_owner: false,
    })
  }

  const handleClickEstimateStartDate = async () => {
    try {
      setEstimatedStartDateLoading(true)
      const { data } = await estimateStartDate(
        assemblyLine?.id || savedAssemblyLine?.id || ''
      )
      if (data.message) {
        setEstimatedMsg(data.message)
        setEstimatedDateTextVisible(true)
      }
      if (data.proposedStartDate) {
        setFormValues({
          ...formValues,
          startDate: moment(data.proposedStartDate),
        })
      }
      setEstimatedStartDateLoading(false)
      setAllocationsChanged(false)
    } catch (error) {
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
      setEstimatedStartDateLoading(false)
    }
  }

  const RequirementsUploaderBox = styled(Box)(() => ({
    display: 'flex',
    gap: '10px',
  }))

  const UploadProgressIndicatorBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  }))

  const [uploadProgress, setUploadProgress] = React.useState<number>(0)

  const handleChangeUploadRequirements = async (event: any) => {
    const formData = new FormData()
    formData.append('thumbnail', false.toString())
    formData.append('file', event.target.files[0])
    try {
      setIsRequirementsUploading(true)
      const { data } = await uploadMedia(formData, (progress: any) => {
        setUploadProgress(progress)
      })
      setFormValues({
        ...formValues,
        requirements: data,
      })
      setIsRequirementsUploading(false)
      setUploadProgress(0)
    } catch (error: any) {
      setIsRequirementsUploading(false)
      addAlert({
        message: getApiErrorMsg(error) || 'Error Uploading Requirements',
        type: SnackbarType.Error,
      })
    }
  }

  return (
    <div>
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <CreateAssemblyLineDialogTitle onClose={handleClose}>
          {assemblyLine ? 'Edit' : 'Create'} Assembly Line
        </CreateAssemblyLineDialogTitle>
        <DialogContent dividers>
          <SetupFeildsBox>
            <MainSectionLabel>Setup Fields</MainSectionLabel>
            <OrganizationSearchAndSelect
              onChange={handleChangeOrganization}
              value={formValues.organization || null}
              error={formValues.invalid_organization}
              helperText={formValues.invalid_organization ? 'Required' : ''}
            />
            <TextField
              value={formValues.name || ''}
              margin="dense"
              label="Name"
              type="text"
              name="name"
              fullWidth
              variant="outlined"
              onChange={handleChangeFormField}
              error={formValues.invalid_name}
              helperText={formValues.invalid_name ? 'Required' : ''}
              sx={{ marginTop: '18px' }}
            />
            <TextField
              value={formValues.description || ''}
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              variant="outlined"
              error={formValues.invalid_description}
              onChange={handleChangeFormField}
              helperText={formValues.invalid_description ? 'Required' : ''}
              sx={{ marginTop: '15px' }}
              multiline
              rows={4}
            />
            <TextField
              value={formValues.monthlySubscriptionCost || ''}
              margin="dense"
              name="monthlySubscriptionCost"
              label="Client Budget"
              type="number"
              fullWidth
              variant="outlined"
              error={formValues.invalid_monthlySubscriptionCost}
              onChange={handleChangeFormField}
              helperText={
                formValues.invalid_monthlySubscriptionCost ? 'Required' : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              sx={{ marginTop: '15px' }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              gap={2}
              sx={{ marginTop: '15px' }}
            >
              <DatePicker
                label="Start Date"
                sx={{ width: '100%' }}
                value={formValues.startDate}
                onChange={handleChangeStartDate}
                slotProps={{
                  textField: {
                    helperText: formValues.invalid_startDate
                      ? 'Invalid Start Date'
                      : '',
                    error: formValues.invalid_startDate,
                  },
                }}
              />
              <EstimatedStartDateButton
                loading={estimatedStartDateLoading}
                variant="outlined"
                onClick={handleClickEstimateStartDate}
                disabled={!savedAssemblyLine || allocationsChanged}
              >
                Estimate Start Date
              </EstimatedStartDateButton>
            </Box>
            {(!savedAssemblyLine || allocationsChanged) && (
              <EstimateAlert severity="info">
                Add Allocations and Save the Assembly Line before getting
                estimated start date projections
              </EstimateAlert>
            )}
            {estimatedDateTextVisible && (
              <EstimateAlert severity="info">{esitmatedMsg}</EstimateAlert>
            )}
            <Box marginTop="20px" display="flex" justifyContent="space-between" gap={2}>
              <Box width="48%">
                <FormControl fullWidth>
                  <InputLabel id="sku-select-label">
                    SKU Type
                  </InputLabel>
                  <Select
                    labelId="sku-select-label"
                    value={formValues.sku}
                    label="SKU Type"
                    name="sku"
                    onChange={handleChangeSelect}
                    error={formValues.invalid_sku}
                  >
                    {Object.values(SKU).map((value) => (
                      <MenuItem key={value} value={value}>
                        {getSkuDisplayLabel(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formValues.invalid_sku && (
                    <FormHelperText error={formValues.invalid_sku}>
                      Required
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
              {isUsageCreditsSku(formValues.sku) && (
                <Box width="48%">
                  <SubscriptionConfigAutocompleteInline
                    value={formValues.assemblyLineSubscriptionConfigId ?? null}
                    onChange={(id) =>
                      setFormValues({
                        ...formValues,
                        assemblyLineSubscriptionConfigId: id ?? null,
                        invalid_assemblyLineSubscriptionConfigId: false,
                      })
                    }
                    error={formValues.invalid_assemblyLineSubscriptionConfigId}
                    helperText={
                      formValues.sku === SKU.FOUNDRY_APP
                        ? 'Optional — server default subscription template applies when blank'
                        : 'Optional at create — required before agent self-serve activation'
                    }
                    targetSkuFilter={
                      formValues.sku === SKU.FOUNDRY_APP
                        ? 'FOUNDRY_APP'
                        : 'ASSEMBLY_AGENT_SUBSCRIPTION'
                    }
                  />
                </Box>
              )}
            </Box>
            {formValues.organization && (
              <>
                <Box marginTop="15px">
                  <OwnerSearchAndSelect
                    orgId={formValues.organization?.id}
                    value={formValues.owner}
                    onChange={handleChangeOwner}
                    error={formValues.invalid_owner}
                    helperText={formValues.invalid_owner ? 'Select Owner' : ''}
                  />
                </Box>
                <Box marginTop="15px">
                  <OrgMembersSearchAndSelect
                    orgId={formValues.organization?.id}
                    value={formValues.members}
                    onChange={handleChangeMembers}
                    error={formValues.invalid_members}
                    helperText={
                      formValues.invalid_members
                        ? 'Select members'
                        : isOptionalMembersSku(formValues.sku)
                          ? 'Optional'
                          : ''
                    }
                  />
                </Box>
              </>
            )}
            <Box marginTop="15px">
              <CapabilitiesSearchAndSelect
                onChange={handleChangeCapabilities}
                value={formValues.capabilities}
                error={formValues.invalid_capabilities}
                helperText={
                  formValues.invalid_capabilities
                    ? 'Required'
                    : formValues.sku === SKU.FOUNDRY_APP
                      ? 'Optional — Foundry apps inherit provisioned capabilities on the project line'
                      : ''
                }
              />
            </Box>
            <Box marginTop="20px">
              <RequirementsUploaderBox>
                <LoadingButton
                  variant="contained"
                  component="label"
                  sx={{ boxShadow: 'none' }}
                  loading={isRequirementsUploading}
                >
                  {formValues.requirements &&
                    (formValues.requirements as Media).media
                    ? 'Change Requirements PDF'
                    : 'Add Requirements PDF'}
                  <input
                    type="file"
                    hidden
                    value={''}
                    onChange={handleChangeUploadRequirements}
                    disabled={isRequirementsUploading}
                    accept="application/pdf"
                  />
                </LoadingButton>
                {isRequirementsUploading && (
                  <UploadProgressIndicatorBox>
                    <CircularProgressWithLabel value={uploadProgress} />
                    <Typography variant="body2">
                      {isRequirementsUploading && uploadProgress < 100
                        ? 'Uploading...'
                        : 'Processing...'}
                    </Typography>
                  </UploadProgressIndicatorBox>
                )}
              </RequirementsUploaderBox>
            </Box>
            <Box marginTop="20px">
              {formValues.requirements &&
                (formValues.requirements as Media).media && (
                  <a
                    style={{ marginTop: '20px' }}
                    href={getMediaUrl((formValues.requirements as Media).media)}
                    target="_blank"
                  >
                    {' '}
                    Requirements
                  </a>
                )}
            </Box>
          </SetupFeildsBox>
          <Box marginTop="20px">
            <MainSectionLabel>Other Fields</MainSectionLabel>
            <FormControl fullWidth>
              <InputLabel id="type-select-label">Timezone</InputLabel>
              <Select
                labelId="type-select-label"
                id="type-select"
                value={formValues.timezone || ''}
                label="Timezone"
                name="timezone"
                onChange={handleChangeSelect}
              >
                {timezones.map((timezone, index) => (
                  <MenuItem key={index} value={timezone.value}>
                    {timezone.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box marginTop="15px">
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.restricted}
                    onChange={(event) =>
                      setFormValues({
                        ...formValues,
                        restricted: event.target.checked,
                      })
                    }
                    name="restricted"
                  />
                }
                label="Restricted"
              />
            </Box>
          </Box>
          <Box marginTop="25px">
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h5" fontWeight={500}>
                  Allocations
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsAllocationFormVisible(true)
                    setEstimatedDateTextVisible(false)
                  }}
                  sx={{ opacity: isAllocationFormVisible ? 0 : 1 }}
                  disabled={isAllocationFormVisible}
                >
                  Add Allocation
                </Button>
              </Box>
              {isOptionalAllocationsSku(formValues.sku) && (
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                  Resource allocations are optional for Assembly Orchestration, Assembly Agent Subscription, and Foundry App projects.
                </Typography>
              )}
            </Box>
          </Box>
          {isAllocationFormVisible && (
            <Paper sx={{ padding: 2 }}>
              <ResourceSearchAndSelect
                value={resource}
                onChange={handleChangeResource}
                error={allocationFormValues.invalid_resourceID}
                helperText={
                  allocationFormValues.invalid_resourceID
                    ? 'Please select a resource'
                    : ''
                }
                resourceID={
                  selectedAllocation.allocation?.resourceID
                    ? selectedAllocation.allocation.resourceID
                    : null
                }
              />
              <Box display="flex" flexDirection="row" gap={3} marginTop="18px">
                <TextField
                  value={allocationFormValues.amount || ''}
                  margin="dense"
                  label="Amount (in $ USD)"
                  type="number"
                  name="amount"
                  fullWidth
                  variant="outlined"
                  onChange={handleChangeAllocationFormField}
                  error={allocationFormValues.invalid_amount}
                  helperText={
                    allocationFormValues.invalid_amount ? 'Required' : ''
                  }
                  sx={{ margin: 0 }}
                />
                <TextField
                  value={allocationFormValues.rate}
                  margin="dense"
                  name="rate"
                  label={`Rate (in ${allocationFormValues.rateUnit.toLowerCase()})`}
                  type="number"
                  fullWidth
                  variant="outlined"
                  error={allocationFormValues.invalid_rate}
                  onChange={handleChangeAllocationFormField}
                  helperText={
                    allocationFormValues.invalid_rate ? 'Required' : ''
                  }
                  sx={{ margin: 0 }}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                marginTop="18px"
              >
                <Box width="48%">
                  <FormControl fullWidth>
                    <InputLabel id="rateUnit-select-label">
                      Rate Unit
                    </InputLabel>
                    <Select
                      labelId="rateUnit-select-label"
                      value={allocationFormValues.rateUnit}
                      label="Rate Unit"
                      name="rateUnit"
                      onChange={handleChangeAllocationFormSelect}
                      error={allocationFormValues.invalid_rateUnit}
                    >
                      {Object.values(RateUnit).map((value) => (
                        <MenuItem key={value} value={value}>
                          {capitalize(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {allocationFormValues.invalid_rateUnit && (
                      <FormHelperText
                        error={allocationFormValues.invalid_rateUnit}
                      >
                        Required
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box width="48%">
                  <FormControl fullWidth>
                    <InputLabel id="frequency-select-label">
                      Frequency (Weekly / Monthly)
                    </InputLabel>
                    <Select
                      labelId="frequency-select-label"
                      value={allocationFormValues.frequency}
                      label="Frequency (Weekly / Monthly)"
                      name="frequency"
                      onChange={handleChangeAllocationFormSelect}
                      error={allocationFormValues.invalid_frequency}
                    >
                      {Object.values(Frequency).map((value) => (
                        <MenuItem key={value} value={value}>
                          {capitalize(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {allocationFormValues.invalid_frequency && (
                      <FormHelperText
                        error={allocationFormValues.invalid_frequency}
                      >
                        Required
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                marginTop="18px"
              >
                <Box width="48%">
                  <FormControl fullWidth>
                    <InputLabel id="type-select-label">Type</InputLabel>
                    <Select
                      labelId="type-select-label"
                      value={allocationFormValues.type}
                      label="Type"
                      name="type"
                      onChange={handleChangeAllocationFormSelect}
                      error={allocationFormValues.invalid_type}
                    >
                      {Object.values(Type).map((value) => (
                        <MenuItem key={value} value={value}>
                          {capitalize(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {allocationFormValues.invalid_type && (
                      <FormHelperText error={allocationFormValues.invalid_type}>
                        Required
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box width="48%">
                  <FormControl fullWidth>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={allocationFormValues.status || Status.Initialized}
                      label="Status"
                      name="status"
                      onChange={handleChangeAllocationFormSelect}
                      error={allocationFormValues.invalid_status}
                    >
                      {Object.values(Status).map((value) => (
                        <MenuItem key={value} value={value}>
                          {capitalize(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {allocationFormValues.invalid_status && (
                      <FormHelperText
                        error={allocationFormValues.invalid_status}
                      >
                        Required
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                marginTop="18px"
              >
                <Box width="48%">
                  <FormControl fullWidth>
                    <TextField
                      id="date"
                      type="date"
                      label="Start Date"
                      name="startDate"
                      value={allocationFormValues.startDate}
                      onChange={handleChangeAllocationFormField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </FormControl>
                </Box>
                <Box width="48%">
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allocationFormValues.startDateAccepted}
                          onChange={(event) => {
                            setAllocationFormValues({
                              ...allocationFormValues,
                              startDateAccepted: event.target.checked,
                            })
                          }}
                        />
                      }
                      label="Start Date Accepted"
                    />
                  </FormControl>
                </Box>
              </Box>

              <TextField
                value={allocationFormValues.statusMessage || ''}
                margin="dense"
                name="statusMessage"
                label="Status Message ( System Generated )"
                type="text"
                fullWidth
                variant="outlined"
                disabled={true}
                onChange={handleChangeAllocationFormField}
                sx={{ margin: 0, marginTop: '18px' }}
              />
              <Box
                display="flex"
                alignItems="center"
                height={65}
                marginTop="18px"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={allocationFormValues.approved}
                      onChange={(event) => {
                        setAllocationFormValues({
                          ...allocationFormValues,
                          approved: event.target.checked,
                          approvedBy: '',
                        })
                      }}
                    />
                  }
                  label="Approved"
                />
                {allocationFormValues.approved && (
                  <TextField
                    value={allocationFormValues.approvedBy || ''}
                    margin="dense"
                    name="approvedBy"
                    label="Approved By"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={handleChangeAllocationFormField}
                  />
                )}
              </Box>
              <Box marginTop="20px">
                <FormControlLabel
                  control={
                    <Switch
                      checked={allocationFormValues.active}
                      onChange={(event) => {
                        setAllocationFormValues({
                          ...allocationFormValues,
                          active: event.target.checked,
                        })
                      }}
                    />
                  }
                  label="Active"
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" marginTop={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAllocationFormValues(allocationFormInitialValues)
                    setIsAllocationFormVisible(false)
                    setResource(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAllocation}
                  sx={{ marginLeft: 1 }}
                >
                  Save Allocation
                </Button>
              </Box>
            </Paper>
          )}
          <List>
            {formValues.allocations.map((allocation, index) => (
              <Paper key={index}>
                <ListItem
                  secondaryAction={
                    <Box display="flex" gap={3}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() =>
                          handleClickEditAllocation(allocation, index)
                        }
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleClickDeleteAllocation(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      {...stringAvatar(allocation.resource?.firstName || '')}
                      src={getMediaUrl(
                        allocation.resource?.profilePicture?.media || ''
                      )}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      allocation.resource?.firstName +
                      ' ' +
                      allocation.resource?.lastName
                    }
                    secondary={capitalize(allocation.type)}
                  />
                </ListItem>
                {formValues.allocations.length - 1 !== index && <Divider />}
              </Paper>
            ))}
          </List>
          <Divider />
          <Box marginTop={3}>
            <Typography variant="h5" fontWeight={500}>
              Funding
            </Typography>
            <TextField
              value={formValues.funding.billingAmount || ''}
              margin="dense"
              name="billingAmount"
              label="Billing Amount"
              type="number"
              fullWidth
              variant="outlined"
              error={formValues.funding.invalid_billingAmount}
              onChange={handleChangeFundingFormField}
              helperText={
                formValues.funding.invalid_billingAmount ? 'Required' : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {currencySymbols[formValues.funding.currency]}
                  </InputAdornment>
                ),
              }}
              sx={{ marginTop: '18px' }}
            />
            <TextField
              value={formValues.funding.statusMsg || ''}
              margin="dense"
              name="statusMsg"
              label="Status Msg (System Generated)"
              disabled={true}
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFundingFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <TextField
              value={formValues.funding.setupLink || ''}
              margin="dense"
              name="setupLink"
              label="Setup Link (optional)"
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFundingFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <TextField
              value={formValues.funding.paymentLink || ''}
              margin="dense"
              name="paymentLink"
              label="Payment Link (optional)"
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFundingFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <FormControl
              fullWidth
              variant="outlined"
              error={formValues.funding.invalid_projectType}
              sx={{ marginTop: '18px' }}
            >
              <InputLabel>Project Type</InputLabel>
              <Select
                value={formValues.funding.projectType}
                onChange={handleChangeFundingProjectType}
                label="Project Type"
                name="projectType"
              >
                {Object.values(ProjectTypes).map((projectType) => (
                  <MenuItem key={projectType} value={projectType}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography>{projectType.replace(/_/g, ' ')}</Typography>
                      <Tooltip
                        title={ProjectTypeDescriptions[projectType]}
                        placement="right"
                        arrow
                        sx={{ maxWidth: 400 }}
                      >
                        <InfoIcon
                          fontSize="small"
                          sx={{
                            color: 'primary.main',
                            cursor: 'help',
                            ml: 1
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formValues.funding.invalid_projectType && (
                <FormHelperText>Required</FormHelperText>
              )}
            </FormControl>
            <Box display="flex" alignItems="center" height={65} marginTop="18px">
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.funding.createContractorInvoices}
                    onChange={handleChangeFundingCreateContractorInvoices}
                  />
                }
                label="Create Contractor Invoices"
              />
            </Box>
            <Box display="flex" alignItems="center" height={65} marginTop="18px">
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.funding.disableExternalInvoiceIntegration}
                    onChange={handleChangeFundingDisableExternalInvoiceIntegration}
                  />
                }
                label="Disable External Invoice Integration"
              />
            </Box>

            {/* Token Pricing Section */}
            <Box marginTop={4}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="token-pricing-content"
                  id="token-pricing-header"
                >
                  <Typography variant="h6" fontWeight={500}>
                    Token Pricing
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    value={formValues.funding.tokenPricing?.inputTokensPerMillion || ''}
                    margin="dense"
                    label="Input Tokens per Million (USD)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeTokenPricingField('inputTokensPerMillion', e.target.value ? parseFloat(e.target.value) : null)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.tokenPricing?.outputTokensPerMillion || ''}
                    margin="dense"
                    label="Output Tokens per Million (USD)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeTokenPricingField('outputTokensPerMillion', e.target.value ? parseFloat(e.target.value) : null)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.tokenPricing?.voiceMinutesPerMinute || ''}
                    margin="dense"
                    label="Voice Minutes per Minute (USD)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeTokenPricingField('voiceMinutesPerMinute', e.target.value ? parseFloat(e.target.value) : null)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.tokenPricing?.totalCredits || ''}
                    margin="dense"
                    label="Total Credits"
                    type="number"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeTokenPricingField('totalCredits', e.target.value ? parseInt(e.target.value) : null)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={tokenPricingCreditsValueDollars}
                    margin="dense"
                    label="Total Credits Monetary Value"
                    type="number"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setTokenPricingCreditsValueDollars(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText="Amount in dollars (will be converted to cents)"
                    sx={{ marginTop: '18px' }}
                  />
                  <DateTimePicker
                    label="Credits Start Time (Optional)"
                    value={tokenPricingCreditsStartTime}
                    onChange={(newValue) => setTokenPricingCreditsStartTime(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        helperText: 'Start time of earliest active credit grant (will be converted to Unix timestamp)',
                      },
                    }}
                    sx={{ marginTop: '18px' }}
                  />
                  <DateTimePicker
                    label="Credits End Time (Optional)"
                    value={tokenPricingCreditsEndTime}
                    onChange={(newValue) => setTokenPricingCreditsEndTime(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        helperText: 'End time of latest expiring credit grant (will be converted to Unix timestamp)',
                      },
                    }}
                    sx={{ marginTop: '18px' }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Stripe Metadata Section */}
            <Box marginTop={4}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="stripe-metadata-content"
                  id="stripe-metadata-header"
                >
                  <Typography variant="h6" fontWeight={500}>
                    Stripe Integration Metadata
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_ACCOUNT_ID || ''}
                    margin="dense"
                    label="Stripe Account ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_ACCOUNT_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_CUSTOMER_ID || ''}
                    margin="dense"
                    label="Stripe Customer ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_CUSTOMER_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_PAYMENT_METHOD_ID || ''}
                    margin="dense"
                    label="Stripe Payment Method ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_PAYMENT_METHOD_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_SUBSCRIPTION_ID || ''}
                    margin="dense"
                    label="Stripe Subscription ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_SUBSCRIPTION_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_INVOICE_ID || ''}
                    margin="dense"
                    label="Stripe Invoice ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_INVOICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_SETUP_PRODUCT_ID || ''}
                    margin="dense"
                    label="Stripe Setup Product ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_SETUP_PRODUCT_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_SETUP_PRICE_ID || ''}
                    margin="dense"
                    label="Stripe Setup Price ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_SETUP_PRICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_SETUP_ONE_TIME_PRICE_ID || ''}
                    margin="dense"
                    label="Stripe Setup One-Time Price ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_SETUP_ONE_TIME_PRICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_LLM_INPUT_TOKENS_METER_ID || ''}
                    margin="dense"
                    label="Stripe LLM Input Tokens Meter ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_LLM_INPUT_TOKENS_METER_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_LLM_OUTPUT_TOKENS_METER_ID || ''}
                    margin="dense"
                    label="Stripe LLM Output Tokens Meter ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_LLM_OUTPUT_TOKENS_METER_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_CONVERSATION_VOICE_METER_ID || ''}
                    margin="dense"
                    label="Stripe Conversation Voice Meter ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_CONVERSATION_VOICE_METER_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_LLM_INPUT_TOKENS_PRICE_ID || ''}
                    margin="dense"
                    label="Stripe LLM Input Tokens Price ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_LLM_INPUT_TOKENS_PRICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_LLM_OUTPUT_TOKENS_PRICE_ID || ''}
                    margin="dense"
                    label="Stripe LLM Output Tokens Price ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_LLM_OUTPUT_TOKENS_PRICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                  <TextField
                    value={formValues.funding.metaData?.STRIPE_CONVERSATION_VOICE_PRICE_ID || ''}
                    margin="dense"
                    label="Stripe Conversation Voice Price ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => handleChangeMetaDataField('STRIPE_CONVERSATION_VOICE_PRICE_ID', e.target.value)}
                    sx={{ marginTop: '18px' }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Box marginTop={3}>
            <Typography variant="h5" fontWeight={500}>
              Dashboard Settings
            </Typography>
            <FormControl fullWidth sx={{ marginTop: '18px' }}>
              <InputLabel id="dashboard-mode-select-label">Dashboard Mode</InputLabel>
              <Select
                labelId="dashboard-mode-select-label"
                value={formValues.dashboardSettings.mode}
                label="Dashboard Mode"
                name="dashboardMode"
                onChange={(event) => {
                  setFormValues({
                    ...formValues,
                    dashboardSettings: {
                      mode: event.target.value as DashboardMode,
                    },
                  })
                }}
              >
                <MenuItem value={DashboardMode.SelfServiceAgents}>
                  Self-Service Agents
                </MenuItem>
                <MenuItem value={DashboardMode.AgenticAutomation}>
                  Agentic Automation
                </MenuItem>
                <MenuItem value={DashboardMode.SelfServiceAgentsAgenticAutomation}>
                  Self-Service Agents + Agentic Automation
                </MenuItem>
              </Select>
              <FormHelperText>
                Controls the operational mode of the assembly line dashboard
              </FormHelperText>
            </FormControl>
          </Box>
          <Box marginTop={3}>
            <Typography variant="h5" fontWeight={500}>
              Project Management
            </Typography>
            <Box display="flex" alignItems="center" height={65} marginTop="18px">
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.enableProjectManagement}
                    onChange={handleChangeEnableProjectManagement}
                  />
                }
                label="Enable Project Management"
              />
            </Box>
            <TextField
              value={formValues.projectManagementLink || ''}
              margin="dense"
              name="projectManagementLink"
              label="Project Management Link (optional)"
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <TextField
              value={formValues.submitTaskLink || ''}
              margin="dense"
              name="submitTaskLink"
              label="Submit Task Link (optional)"
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <TextField
              value={formValues.kanbanLink || ''}
              margin="dense"
              name="kanbanLink"
              label="Kanban Link (optional)"
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFormField}
              sx={{ margin: 0, marginTop: '18px' }}
            />
            <Box display="flex" justifyContent="space-between" gap={2}>
              <TextField
                value={formValues.teamID || ''}
                margin="dense"
                name="teamID"
                label="Team ID"
                disabled={true}
                type="text"
                fullWidth
                variant="outlined"
                onChange={handleChangeFormField}
                sx={{ margin: 0, marginTop: '18px' }}
              />
              <TextField
                value={formValues.projectID || ''}
                margin="dense"
                name="projectID"
                label="Project ID"
                disabled={true}
                type="text"
                fullWidth
                variant="outlined"
                onChange={handleChangeFormField}
                sx={{ margin: 0, marginTop: '18px' }}
              />
            </Box>
          </Box>
          {assemblyLine && (
            <Box marginTop={5}>
              <Typography>
                Billing Setup Complete:{' '}
                {assemblyLine.org?.billing.billingSetupComplete ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
          <Box marginTop={assemblyLine ? 3 : 5}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Assembly Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={formValues.status || ''}
                label="Assembly Status"
                name="status"
                onChange={(event) => {
                  handleChangeSelect(event)
                  setResource(null)
                  setAllocationFormValues({
                    ...allocationFormValues,
                    invalid_resourceID: false,
                  })
                }}
                error={formValues.invalid_status}
              >
                {Object.values(AssemblyLineStatus).map((value) => (
                  <MenuItem key={value} value={value}>
                    {capitalize(value)}
                  </MenuItem>
                ))}
              </Select>
              {formValues.invalid_status && (
                <FormHelperText error={formValues.invalid_status}>
                  Required
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box>
            <TextField
              value={formValues.statusMessage || ''}
              margin="dense"
              name="statusMessage"
              label="Status Message (System Generated)"
              disabled={true}
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChangeFormField}
              sx={{ marginTop: '18px' }}
            />
          </Box>
          <Box display="flex" alignItems="center" height={65} marginTop="18px">
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.approved}
                  onChange={(event) => {
                    setFormValues({
                      ...formValues,
                      approved: event.target.checked,
                      approvedBy: '',
                    })
                  }}
                />
              }
              label="Approved"
            />
            {formValues.approved && (
              <TextField
                value={formValues.approvedBy || ''}
                margin="dense"
                name="approvedBy"
                label="Approved By"
                type="text"
                fullWidth
                variant="outlined"
                onChange={handleChangeFormField}
              />
            )}
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
            sx={{ marginTop: '15px' }}
          >
            <DatePicker
              label="End Date"
              sx={{ width: '100%' }}
              value={formValues.endDate}
              onChange={handleChangeEndDate}
              slotProps={{
                textField: {
                  helperText: formValues.invalid_endDate
                    ? 'Invalid End Date'
                    : '',
                  error: formValues.invalid_endDate,
                },
              }}
            />
          </Box>
          {assemblyLine && (
            <Box marginTop={3}>
              {Object.values(checkListItems).length > 0 && (
                <Typography variant="h5" fontWeight={500} marginBottom={1}>
                  Checklist
                </Typography>
              )}
              <Box display="flex" flexWrap="wrap" gap={4}>
                {Object.values(checkListItems).map((item: any) => (
                  <FormControlLabel
                    key={item.key}
                    control={
                      <Switch
                        checked={item.done}
                        onChange={(event) => {
                          setCheckListItems({
                            ...checkListItems,
                            [item.key]: {
                              ...item,
                              done: event.target.checked,
                            },
                          })
                        }}
                      />
                    }
                    label={item.title}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {assemblyLine && (
            <Button
              startIcon={<ReceiptIcon />}
              onClick={() => {
                navigate(interpolateRouteUtil(RoutePaths.UsageBillingWithAssembly, { id: assemblyLine.id }))
                handleClose()
              }}
            >
              Subscription and Usage Details
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            disabled={isAllocationFormVisible}
            loading={isSaving}
            onClick={handleClickSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
